import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Download, FileText, FileBadge, ShieldCheck, ScrollText, Sparkles } from "lucide-react";
import { PortalHeader, PortalEmpty, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProject, listMyDocuments, getMyFileSignedUrl, generateMyProposalPdf } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/documents")({
  component: DocumentsPage,
});

const KIND_LABEL: Record<string, { label: string; icon: any }> = {
  proposal: { label: "Proposal", icon: FileBadge },
  estimate: { label: "Estimate", icon: FileText },
  invoice: { label: "Invoice", icon: ScrollText },
  warranty: { label: "Warranty", icon: ShieldCheck },
  care_instructions: { label: "Care instructions", icon: Sparkles },
  paint_schedule: { label: "Paint schedule", icon: FileText },
};

function fmtSize(b: number | null) { if (!b) return "—"; if (b < 1024) return `${b} B`; if (b < 1024*1024) return `${(b/1024).toFixed(0)} KB`; return `${(b/1024/1024).toFixed(1)} MB`; }

function DocumentsPage() {
  const projectFn = useServerFn(getMyProject);
  const listFn = useServerFn(listMyDocuments);
  const signFn = useServerFn(getMyFileSignedUrl);
  const pdfFn = useServerFn(generateMyProposalPdf);

  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const hasProposal = !!projectQ.data?.project?.proposal_id;
  const docsQ = useQuery({
    queryKey: ["portal", "docs", projectId],
    queryFn: () => listFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  const [busyId, setBusyId] = useState<string | null>(null);

  if (projectQ.isLoading || docsQ.isLoading)
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const docs = docsQ.data ?? [];

  const open = async (d: typeof docs[number]) => {
    setBusyId(d.id);
    try {
      let url = d.file_url;
      if (!/^https?:\/\//i.test(url)) {
        const r = await signFn({ data: { projectId, path: url } });
        url = r.url;
      }
      window.open(url, "_blank");
    } catch (e: any) { alert(e?.message || "Could not open"); }
    finally { setBusyId(null); }
  };

  const downloadProposal = async () => {
    setBusyId("__proposal__");
    try {
      const r = await pdfFn({ data: { projectId } });
      const bin = atob(r.base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: r.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { alert(e?.message || "PDF generation failed"); }
    finally { setBusyId(null); }
  };

  const downloadAll = async () => {
    for (const d of docs) {
      try {
        let url = d.file_url;
        if (!/^https?:\/\//i.test(url)) {
          const r = await signFn({ data: { projectId, path: url } });
          url = r.url;
        }
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        await new Promise((r) => setTimeout(r, 250));
      } catch {}
    }
  };

  return (
    <>
      <PortalHeader
        eyebrow="Document Center"
        title="Project documents"
        description="Every PDF and file connected to your project. Stored privately — only you and our team can access them."
        actions={
          <button onClick={downloadAll} disabled={!docs.length}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-50 px-4 py-2 text-xs text-zinc-200">
            <Download className="size-3.5" /> Download all
          </button>
        }
      />

      {hasProposal && (
        <PortalCard className="p-5 mb-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-10 rounded-xl bg-amber-300/10 border border-amber-300/20 grid place-items-center"><FileBadge className="size-5 text-amber-300" /></div>
            <div>
              <p className="text-sm text-white font-medium">Active proposal</p>
              <p className="text-xs text-zinc-500">Generated on demand — always the latest version</p>
            </div>
          </div>
          <button onClick={downloadProposal} disabled={busyId === "__proposal__"} className="rounded-full bg-amber-300 hover:bg-amber-200 text-zinc-950 px-4 py-2 text-xs font-semibold inline-flex items-center gap-2">
            {busyId === "__proposal__" ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />} PDF
          </button>
        </PortalCard>
      )}

      {docs.length === 0 ? (
        <PortalEmpty title="No documents yet" description="Estimates, warranties, care instructions, and receipts will appear here." />
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const meta = KIND_LABEL[d.kind] ?? { label: d.kind, icon: FileText };
            const Icon = meta.icon;
            return (
              <PortalCard key={d.id} className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-10 rounded-xl bg-white/5 grid place-items-center"><Icon className="size-5 text-amber-300" /></div>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{d.title}</p>
                    <p className="text-[11px] text-zinc-500">{meta.label} · {fmtSize(d.file_size_bytes)} · {new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <button onClick={() => open(d)} disabled={busyId === d.id}
                  className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-zinc-200 inline-flex items-center gap-1.5 shrink-0">
                  {busyId === d.id ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />} Open
                </button>
              </PortalCard>
            );
          })}
        </div>
      )}
    </>
  );
}